/**
 * Dietary Restriction Agent
 * Filters and recommends products based on dietary needs
 */

interface DietaryProfile {
  restrictions: DietaryRestriction[];
  preferences: DietaryPreference[];
  allergies: Allergy[];
  goals: HealthGoal[];
}

type DietaryRestriction =
  | 'vegetarian'
  | 'vegan'
  | 'pescatarian'
  | 'kosher'
  | 'halal'
  | 'gluten-free'
  | 'dairy-free'
  | 'keto'
  | 'paleo'
  | 'low-sodium'
  | 'low-sugar'
  | 'diabetic-friendly';

type DietaryPreference =
  | 'organic'
  | 'non-gmo'
  | 'locally-sourced'
  | 'sustainable'
  | 'seasonal'
  | 'whole-foods'
  | 'raw'
  | 'high-protein'
  | 'low-carb';

type Allergy =
  | 'peanuts'
  | 'tree-nuts'
  | 'shellfish'
  | 'fish'
  | 'eggs'
  | 'milk'
  | 'soy'
  | 'wheat'
  | 'sesame';

type HealthGoal =
  | 'weight-loss'
  | 'muscle-gain'
  | 'heart-health'
  | 'digestive-health'
  | 'immune-support'
  | 'energy-boost'
  | 'anti-inflammatory';

interface Product {
  id: string;
  name: string;
  category: string;
  tags: string[];
  ingredients?: string[];
  allergens?: Allergy[];
  nutritionInfo?: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    sodium: number;
    sugar: number;
    fiber: number;
  };
}

interface FilterResult {
  compatible: Product[];
  restricted: { product: Product; reasons: string[] }[];
  warnings: { product: Product; warnings: string[] }[];
}

class DietaryRestrictionAgent {
  private restrictionRules: { [key: string]: { avoid: string[]; prefer: string[] } } = {
    vegetarian: {
      avoid: ['meat', 'poultry', 'fish', 'gelatin'],
      prefer: ['vegetables', 'fruits', 'grains', 'legumes', 'dairy', 'eggs'],
    },
    vegan: {
      avoid: ['meat', 'poultry', 'fish', 'dairy', 'eggs', 'honey', 'gelatin'],
      prefer: ['vegetables', 'fruits', 'grains', 'legumes', 'nuts', 'seeds'],
    },
    pescatarian: {
      avoid: ['meat', 'poultry'],
      prefer: ['fish', 'seafood', 'vegetables', 'fruits'],
    },
    'gluten-free': {
      avoid: ['wheat', 'barley', 'rye', 'oats', 'bread', 'pasta'],
      prefer: ['rice', 'corn', 'quinoa', 'potatoes', 'fruits', 'vegetables'],
    },
    'dairy-free': {
      avoid: ['milk', 'cheese', 'butter', 'cream', 'yogurt', 'whey'],
      prefer: ['plant milk', 'coconut', 'vegetables', 'fruits'],
    },
    keto: {
      avoid: ['sugar', 'bread', 'pasta', 'rice', 'potatoes', 'high-carb'],
      prefer: ['meat', 'fish', 'eggs', 'cheese', 'leafy greens', 'avocado'],
    },
    paleo: {
      avoid: ['grains', 'legumes', 'dairy', 'processed foods', 'sugar'],
      prefer: ['meat', 'fish', 'vegetables', 'fruits', 'nuts', 'seeds'],
    },
    'low-sodium': {
      avoid: ['processed foods', 'cured meats', 'canned goods', 'pickled items'],
      prefer: ['fresh produce', 'herbs', 'unsalted items'],
    },
    'low-sugar': {
      avoid: ['candy', 'soda', 'fruit juice', 'sweetened items', 'honey'],
      prefer: ['vegetables', 'proteins', 'whole grains'],
    },
    'diabetic-friendly': {
      avoid: ['high-sugar', 'refined carbs', 'sweetened beverages'],
      prefer: ['non-starchy vegetables', 'lean protein', 'whole grains', 'berries'],
    },
  };

  /**
   * Filter products based on dietary profile
   */
  filterProducts(products: Product[], profile: DietaryProfile): FilterResult {
    const compatible: Product[] = [];
    const restricted: { product: Product; reasons: string[] }[] = [];
    const warnings: { product: Product; warnings: string[] }[] = [];

    products.forEach(product => {
      const { isCompatible, restrictionReasons, warningMessages } =
        this.checkProductCompatibility(product, profile);

      if (restrictionReasons.length > 0) {
        restricted.push({ product, reasons: restrictionReasons });
      } else if (warningMessages.length > 0) {
        warnings.push({ product, warnings: warningMessages });
        compatible.push(product);
      } else {
        compatible.push(product);
      }
    });

    return { compatible, restricted, warnings };
  }

  /**
   * Check product compatibility with dietary profile
   */
  checkProductCompatibility(
    product: Product,
    profile: DietaryProfile
  ): { isCompatible: boolean; restrictionReasons: string[]; warningMessages: string[] } {
    const restrictionReasons: string[] = [];
    const warningMessages: string[] = [];

    // Check allergies first (most critical)
    if (product.allergens && profile.allergies.length > 0) {
      const matchingAllergens = product.allergens.filter(a =>
        profile.allergies.includes(a)
      );
      if (matchingAllergens.length > 0) {
        restrictionReasons.push(`Contains allergens: ${matchingAllergens.join(', ')}`);
      }
    }

    // Check dietary restrictions
    profile.restrictions.forEach(restriction => {
      const rules = this.restrictionRules[restriction];
      if (rules) {
        const productTags = product.tags.map(t => t.toLowerCase());
        const productName = product.name.toLowerCase();

        rules.avoid.forEach(avoidItem => {
          if (productTags.includes(avoidItem) || productName.includes(avoidItem)) {
            restrictionReasons.push(`Not ${restriction}: contains ${avoidItem}`);
          }
        });
      }
    });

    // Check nutrition goals
    if (product.nutritionInfo) {
      profile.goals.forEach(goal => {
        const warnings = this.checkNutritionGoal(product.nutritionInfo!, goal);
        warningMessages.push(...warnings);
      });
    }

    return {
      isCompatible: restrictionReasons.length === 0,
      restrictionReasons,
      warningMessages,
    };
  }

  /**
   * Check if product nutrition aligns with health goals
   */
  private checkNutritionGoal(
    nutrition: Product['nutritionInfo'] & {},
    goal: HealthGoal
  ): string[] {
    const warnings: string[] = [];

    switch (goal) {
      case 'weight-loss':
        if (nutrition.calories > 300) warnings.push('High calorie');
        if (nutrition.sugar > 15) warnings.push('High sugar');
        break;
      case 'muscle-gain':
        if (nutrition.protein < 10) warnings.push('Low protein');
        break;
      case 'heart-health':
        if (nutrition.sodium > 500) warnings.push('High sodium');
        if (nutrition.fat > 20) warnings.push('High fat');
        break;
      case 'digestive-health':
        if (nutrition.fiber < 3) warnings.push('Low fiber');
        break;
    }

    return warnings;
  }

  /**
   * Get recommended products for a dietary profile
   */
  getRecommendations(
    products: Product[],
    profile: DietaryProfile
  ): { product: Product; score: number; reasons: string[] }[] {
    const recommendations: { product: Product; score: number; reasons: string[] }[] = [];

    products.forEach(product => {
      const { isCompatible, restrictionReasons } =
        this.checkProductCompatibility(product, profile);

      if (!isCompatible) return;

      let score = 50;
      const reasons: string[] = [];

      // Boost score for preference matches
      profile.preferences.forEach(pref => {
        if (product.tags.includes(pref)) {
          score += 10;
          reasons.push(`Matches preference: ${pref}`);
        }
      });

      // Check health goal alignment
      profile.goals.forEach(goal => {
        const alignment = this.checkGoalAlignment(product, goal);
        score += alignment.score;
        if (alignment.reason) reasons.push(alignment.reason);
      });

      if (score > 50) {
        recommendations.push({ product, score, reasons });
      }
    });

    return recommendations.sort((a, b) => b.score - a.score);
  }

  /**
   * Check how well a product aligns with a health goal
   */
  private checkGoalAlignment(
    product: Product,
    goal: HealthGoal
  ): { score: number; reason?: string } {
    const goalTags: { [key: string]: string[] } = {
      'weight-loss': ['low-calorie', 'high-fiber', 'vegetables', 'lean'],
      'muscle-gain': ['high-protein', 'eggs', 'meat', 'legumes'],
      'heart-health': ['vegetables', 'fruits', 'whole-grains', 'fish'],
      'digestive-health': ['high-fiber', 'fermented', 'vegetables'],
      'immune-support': ['citrus', 'vegetables', 'garlic', 'ginger'],
      'energy-boost': ['complex-carbs', 'iron-rich', 'b-vitamins'],
      'anti-inflammatory': ['omega-3', 'berries', 'leafy-greens', 'turmeric'],
    };

    const relevantTags = goalTags[goal] || [];
    const matchCount = product.tags.filter(t =>
      relevantTags.some(rt => t.toLowerCase().includes(rt))
    ).length;

    if (matchCount > 0) {
      return {
        score: matchCount * 5,
        reason: `Supports ${goal.replace('-', ' ')}`
      };
    }

    return { score: 0 };
  }

  /**
   * Generate dietary profile summary
   */
  generateProfileSummary(profile: DietaryProfile): {
    summary: string;
    avoidList: string[];
    preferList: string[];
    allergenWarning: string | null;
  } {
    const avoidList: string[] = [];
    const preferList: string[] = [];

    profile.restrictions.forEach(restriction => {
      const rules = this.restrictionRules[restriction];
      if (rules) {
        avoidList.push(...rules.avoid);
        preferList.push(...rules.prefer);
      }
    });

    const summary = [
      profile.restrictions.length > 0
        ? `Following: ${profile.restrictions.join(', ')}`
        : null,
      profile.allergies.length > 0
        ? `Allergies: ${profile.allergies.join(', ')}`
        : null,
      profile.goals.length > 0
        ? `Goals: ${profile.goals.map(g => g.replace('-', ' ')).join(', ')}`
        : null,
    ].filter(Boolean).join('. ');

    return {
      summary,
      avoidList: [...new Set(avoidList)],
      preferList: [...new Set(preferList)],
      allergenWarning: profile.allergies.length > 0
        ? `Alert: ${profile.allergies.join(', ')} allergy`
        : null,
    };
  }

  /**
   * Get substitution suggestions
   */
  getSubstitutions(
    restrictedItem: string,
    restriction: DietaryRestriction
  ): string[] {
    const substitutions: { [key: string]: { [key: string]: string[] } } = {
      vegan: {
        milk: ['oat milk', 'almond milk', 'soy milk', 'coconut milk'],
        eggs: ['flax eggs', 'chia eggs', 'applesauce', 'mashed banana'],
        cheese: ['nutritional yeast', 'cashew cheese', 'tofu ricotta'],
        butter: ['coconut oil', 'olive oil', 'vegan butter'],
        honey: ['maple syrup', 'agave nectar', 'date syrup'],
      },
      'gluten-free': {
        bread: ['gluten-free bread', 'lettuce wraps', 'rice cakes'],
        pasta: ['rice noodles', 'zucchini noodles', 'quinoa pasta'],
        flour: ['almond flour', 'coconut flour', 'rice flour'],
      },
      'dairy-free': {
        milk: ['oat milk', 'almond milk', 'coconut milk'],
        cheese: ['nutritional yeast', 'dairy-free cheese'],
        cream: ['coconut cream', 'cashew cream'],
      },
      keto: {
        bread: ['cloud bread', 'lettuce wraps', 'keto bread'],
        pasta: ['zucchini noodles', 'shirataki noodles'],
        rice: ['cauliflower rice', 'broccoli rice'],
        sugar: ['stevia', 'erythritol', 'monk fruit'],
      },
    };

    const normalizedItem = restrictedItem.toLowerCase();
    return substitutions[restriction]?.[normalizedItem] || [];
  }
}

const dietaryRestrictionAgent = new DietaryRestrictionAgent();
export default dietaryRestrictionAgent;
